/**
 * Seasons: the competitive meta-layer over the per-map game.
 *
 * Shared between client and server so both agree on the wire shapes, the
 * three-currency model, the crown-per-rank rule, and — crucially — the
 * award-tier ladder. The ladder is computed (not hard-coded) so it scales
 * from a 12-player launch to a 100k-player season without code changes.
 *
 * Three currencies make up a player's season score:
 *  - stars  ★  : cleared a map (loop 1)            — all-time best, per map
 *  - cups   🏆 : mastered infinite mode (loops 2-3) — all-time best, per map
 *  - crowns 👑 : sit in a map leaderboard's top 3   — LIVE, derived from rank
 *
 * A crown is not stored: it reflects your *current* standing on a map's
 * leaderboard (#1 → 3, #2 → 2, #3 → 1). Overtake someone and you inherit
 * their crowns; get overtaken and you lose yours. The season leaderboard
 * ranks players by `stars + cups + crowns`.
 */

import { GAME_MODES, type GameMode, modeLabel } from './chart.js';

// ---------------------------------------------------------------------------
// Currencies
// ---------------------------------------------------------------------------

/** The three season currencies for one scope (a single mode, or "overall"). */
export interface CurrencyTotals {
  stars: number;
  cups: number;
  crowns: number;
}

/** Season score = the flat sum of all three currencies. */
export function currencyTotal(c: CurrencyTotals): number {
  return c.stars + c.cups + c.crowns;
}

/** A blank currency wallet. */
export const EMPTY_CURRENCY: CurrencyTotals = { stars: 0, cups: 0, crowns: 0 };

/**
 * How many crowns a player holds for finishing a map's leaderboard at
 * `rank` (1-based, among *distinct* players). Ranks below the podium
 * award nothing. Kept in shared/ so the per-map leaderboard panel and the
 * server's crown aggregation never disagree on the podium payout.
 */
export function crownsForRank(rank: number): number {
  if (rank === 1) {
    return 3;
  }
  if (rank === 2) {
    return 2;
  }
  if (rank === 3) {
    return 1;
  }
  return 0;
}

/** Only the top {@link MAX_CROWN_RANK} players on a map earn crowns. */
export const MAX_CROWN_RANK = 3;

// ---------------------------------------------------------------------------
// Scopes (overall + one per mode)
// ---------------------------------------------------------------------------

/**
 * A season surface: the cross-mode "overall" board plus one board per game
 * mode. Every leaderboard / standing / award exists once per scope.
 */
export type SeasonScope = 'overall' | GameMode;

/** Canonical scope order: overall first, then every mode in {@link GAME_MODES}. */
export const SEASON_SCOPES: readonly SeasonScope[] = ['overall', ...GAME_MODES];

/** Human label for a scope ("Overall", "Pulse", "Circles", …). */
export function scopeLabel(scope: SeasonScope): string {
  return scope === 'overall' ? 'Overall' : modeLabel(scope);
}

/** Type guard for routing/query-param validation on the server. */
export function isSeasonScope(value: string): value is SeasonScope {
  return value === 'overall' || (GAME_MODES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Award tiers (programmatically scaled placement badges)
// ---------------------------------------------------------------------------

/**
 * A placement badge: awarded at season end to every player who finishes a
 * scope at `rank <= threshold`. A player keeps only their single most
 * prestigious tier per scope, so the holder of the "Top 2" badge is, in
 * practice, exactly rank 2 (rank 1 holds "Champion").
 */
export interface AwardTier {
  /** Stable id, e.g. `top-100`, `top-1`. */
  id: string;
  /** "Top N" cut-off: `rank <= threshold` qualifies. `1` is the champion. */
  threshold: number;
  /** Display label ("Champion", "Top 2", "Top 100", …). */
  label: string;
}

/**
 * Base prestige ladder the product is designed around: top
 * 1000/500/100/50/10/5/3/2/1. Higher tiers are generated on demand (see
 * {@link buildTierThresholds}) so a huge season still has a meaningful
 * entry-level badge.
 */
const BASE_TIER_THRESHOLDS = [1, 2, 3, 5, 10, 50, 100, 500, 1000] as const;

/**
 * Ascending tier thresholds covering up to `maxNeeded` participants. Below
 * 1000 we use the hand-picked base ladder; above it we extend with a
 * 1–2.5–5 progression (2500, 5000, 10000, 25000, …) so the ladder keeps
 * pace with population without ballooning the badge count.
 */
function buildTierThresholds(maxNeeded: number): number[] {
  const thresholds: number[] = [...BASE_TIER_THRESHOLDS];
  const multipliers = [2.5, 2, 2];
  let value = 1000;
  let step = 0;
  while (value < maxNeeded) {
    value = Math.round(value * multipliers[step % multipliers.length]);
    thresholds.push(value);
    step += 1;
  }
  return thresholds;
}

function awardLabel(threshold: number): string {
  if (threshold === 1) {
    return 'Champion';
  }
  return `Top ${threshold.toLocaleString()}`;
}

function makeTier(threshold: number): AwardTier {
  return { id: `top-${threshold}`, threshold, label: awardLabel(threshold) };
}

/**
 * The award ladder for a scope with `participants` ranked players, ascending
 * by threshold (index 0 = most prestigious = Champion).
 *
 * Only *selective* tiers survive: a "Top N" badge is dropped when
 * `N >= participants`, because a badge everyone earns is no achievement.
 * Champion is always present. This is the "scale programmatically" rule —
 * a 12-player season exposes Champion … Top 10; a 50k-player season also
 * exposes Top 100 … Top 25000.
 */
export function getAwardLadder(participants: number): AwardTier[] {
  if (participants <= 1) {
    return [makeTier(1)];
  }
  // `participants >= 2` here, so the base ladder's `1` always clears the
  // `t < participants` filter — Champion is guaranteed present.
  const thresholds = buildTierThresholds(participants)
    .filter((t) => t < participants)
    .sort((a, b) => a - b);
  return thresholds.map(makeTier);
}

/**
 * Resolve a player's award situation for one scope from their `rank` and the
 * scope's `ladder`:
 *  - `current`: the badge they're on track for if the season ended now (their
 *    most prestigious satisfied tier), or `null` if they're outside every tier.
 *  - `next`: the next badge up to chase, or `null` if they're already Champion.
 *
 * `rank === null` means unranked (no currency yet): no current badge, and the
 * easiest tier becomes the first goal.
 */
export function resolveAwards(
  rank: number | null,
  ladder: readonly AwardTier[],
): { current: AwardTier | null; next: AwardTier | null } {
  if (ladder.length === 0) {
    return { current: null, next: null };
  }
  const entry = ladder[ladder.length - 1];
  if (rank === null) {
    return { current: null, next: entry };
  }
  let currentIdx = -1;
  for (let i = 0; i < ladder.length; i += 1) {
    if (rank <= ladder[i].threshold) {
      currentIdx = i;
      break;
    }
  }
  if (currentIdx === -1) {
    return { current: null, next: entry };
  }
  const current = ladder[currentIdx];
  const next = currentIdx === 0 ? null : ladder[currentIdx - 1];
  return { current, next };
}

// ---------------------------------------------------------------------------
// Wire shapes (API <-> client)
// ---------------------------------------------------------------------------

/** The active season's identity. `startsAt: null` means "since the beginning". */
export interface CurrentSeason {
  id: string;
  seasonNumber: number;
  name: string;
  startsAt: string | null;
}

/** One row of a season leaderboard (overall or per mode). */
export interface SeasonLeaderboardEntry {
  rank: number;
  userUid: string;
  displayName: string | null;
  isAnonymous: boolean;
  stars: number;
  cups: number;
  crowns: number;
  total: number;
}

/**
 * The requesting player's position in one scope. `rank`/`participants` make
 * the "you are #R of N" line explicit; the client derives on-track / next
 * awards from {@link getAwardLadder} + {@link resolveAwards} so the tier
 * logic lives in exactly one place.
 */
export interface SeasonStanding {
  scope: SeasonScope;
  /** 1-based rank, or `null` when the player has earned no currency yet. */
  rank: number | null;
  /** Total ranked players in this scope (the "of N"). */
  participants: number;
  stars: number;
  cups: number;
  crowns: number;
  total: number;
}

/**
 * The signed-in player's live crown count on a single map, derived from their
 * current podium rank on that map's leaderboard (#1 → 3, #2 → 2, #3 → 1).
 * Only maps where the player sits in the top {@link MAX_CROWN_RANK} are
 * returned, so a `crowns` value here is always 1–3. Keyed by `(mode, mapSlug)`
 * because the modes share slugs (e.g. `on-and-on` exists in Pulse and Circles).
 */
export interface SeasonMapCrown {
  mapSlug: string;
  mode: GameMode;
  crowns: number;
}

/** `GET /api/seasons/me`: the player's standing across every scope. */
export interface SeasonMeResponse {
  season: CurrentSeason;
  standings: SeasonStanding[];
  /**
   * Per-map crowns the player currently holds (one entry per podium map).
   * Lets the song-browse trophy strips show a crown on each map the player
   * is ranked top 3 on, alongside their stored stars/cups.
   */
  mapCrowns: SeasonMapCrown[];
}

/** `GET /api/seasons/leaderboard?scope=…`: a single scope's board. */
export interface SeasonLeaderboardResponse {
  season: CurrentSeason;
  scope: SeasonScope;
  participants: number;
  entries: SeasonLeaderboardEntry[];
  /** The requesting player's row, even if outside the returned page. */
  me: SeasonLeaderboardEntry | null;
}

/** Default page size for a season leaderboard (the spec's "top 100"). */
export const SEASON_LEADERBOARD_LIMIT = 100;
